import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FORMULA_OPERATOR_GLYPH,
  FormulaInput,
  Input,
  Label,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  validateFormula,
  type FormulaMetric,
  type FormulaToken,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { MetricDirection, MetricFormat, ReportFormula, type StatsRow } from "@gen/tracker/v1/reporting_pb";

import { derive } from "@/lib/campaigns-table";
import { DASH } from "@/lib/format";
import { toBag } from "@/lib/report";
import { useT, type TKey } from "@/lib/i18n";
import { useCheckReportFormula, useDeleteReportFormula, useSaveReportFormula } from "@/lib/services/report-formula-service";
import { cellText, formatCustom, reportColumns, columnLabel } from "./columns";
import type { ReportingSchema } from "@/lib/services/reporting-schema-service";
import { toastFailure } from "@/lib/plan";

/** The metrics a formula is built from, in the board's palette order. */
const PALETTE = ["clicks", "leads", "conversions", "conversions_hold", "revenue", "cost", "profit", "cta_clicks", "bots"];

/** Tokens → the server's expression text: metric keys, numbers, + - * / ( ). Pure. */
export function tokensToExpression(tokens: readonly FormulaToken[]): string {
  return tokens.map((tk) => (tk.type === "metric" ? tk.id : tk.value)).join(" ");
}

/** The server's canonical text back into tokens. An unknown word stays a metric token, so validation names it. Pure. */
export function expressionToTokens(expression: string): FormulaToken[] {
  const out: FormulaToken[] = [];
  for (const m of expression.matchAll(/[A-Za-z_][\w.]*|\d+(?:\.\d+)?|[-+*/()]/g)) {
    const w = m[0];
    if (/^[-+*/()]$/.test(w)) out.push({ type: "operator", value: w as "+" });
    else if (/^\d/.test(w)) out.push({ type: "number", value: w });
    else out.push({ type: "metric", id: w });
  }
  return out;
}

type Unit = "number" | "money" | "percent";
const UNIT_FORMAT: Record<Unit, MetricFormat> = { number: MetricFormat.DECIMAL, money: MetricFormat.CURRENCY_USD, percent: MetricFormat.PERCENT };
const unitOf = (f: MetricFormat): Unit => (f === MetricFormat.CURRENCY_USD ? "money" : f === MetricFormat.PERCENT ? "percent" : "number");

/**
 * «Своя метрика» (доска ReportFormula): a name, a formula on the package's
 * FormulaInput (a metric is a token holding its key, so a rename never breaks
 * it), how it is shown (unit, digits, which way is better) and «Проверка на
 * текущем отчёте» — the server's CheckReportFormula over this report's totals.
 * Saved, it is one more metric of the workspace: the column arrives from the
 * reloaded schema, in every report and in the campaigns list.
 */
export function FormulaDialog({
  schema,
  rawTotals,
  open,
  onOpenChange,
  formula,
  onCreated,
}: {
  schema: ReportingSchema | undefined;
  /** The totals of the screen the dialog opened from — «Проверка» runs over them. */
  rawTotals: StatsRow | undefined;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Edit this one instead of creating. */
  formula?: ReportFormula;
  onCreated?: (key: string) => void;
}) {
  const t = useT();
  const cols = reportColumns(schema);
  const totals = useMemo(() => {
    const base = toBag(rawTotals);
    return schema && base ? derive(schema.metrics, base) : undefined;
  }, [schema, rawTotals]);
  const metrics: FormulaMetric[] = useMemo(
    () => PALETTE.flatMap((k) => {
      const c = cols.find((x) => x.key === k);
      return c ? [{ id: k, label: columnLabel(t, c) }] : [];
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, t],
  );
  const [name, setName] = useState("");
  const [tokens, setTokens] = useState<FormulaToken[]>([]);
  const [unit, setUnit] = useState<Unit>("money");
  const [decimals, setDecimals] = useState(2);
  const [better, setBetter] = useState<MetricDirection>(MetricDirection.HIGHER);
  useEffect(() => {
    if (!open) return;
    setName(formula?.label ?? "");
    setTokens(formula ? expressionToTokens(formula.expression) : []);
    setUnit(formula ? unitOf(formula.format) : "money");
    setDecimals(formula?.decimals ?? 2);
    setBetter(formula?.better === MetricDirection.LOWER ? MetricDirection.LOWER : MetricDirection.HIGHER);
  }, [open, formula]);

  const problem = tokens.length ? validateFormula(tokens, metrics) : null;
  const expression = problem ? "" : tokensToExpression(tokens);
  const check = useCheckReportFormula(open ? expression : "", rawTotals);
  const save = useSaveReportFormula();
  const remove = useDeleteReportFormula();
  const checkValue = check.data?.value !== undefined ? Number(check.data.value) : undefined;
  const checkText =
    checkValue === undefined
      ? DASH
      : `${formatCustom({ format: UNIT_FORMAT[unit], decimals }, checkValue)}${unit === "money" ? " USD" : ""}`;
  // «(11 586 − 8 425) / 1 266»: the formula over the numbers it was checked on.
  const overTotals = tokens
    .map((tk) => {
      if (tk.type === "operator") return FORMULA_OPERATOR_GLYPH[tk.value];
      if (tk.type === "number") return tk.value;
      const c = cols.find((x) => x.key === tk.id);
      return c ? cellText({ ...c, format: c.format === "profit" ? "money" : c.format }, totals?.[tk.id]) : tk.id;
    })
    .join(" ")
    .replace(/\( /g, "(")
    .replace(/ \)/g, ")");

  const submit = () => {
    save.mutate(new ReportFormula({ id: formula?.id ?? "", key: formula?.key ?? "", label: name.trim(), expression, format: UNIT_FORMAT[unit], decimals, better }), {
      onSuccess: (f) => {
        toast.success(t(formula ? "rep.formula.updated" : "rep.formula.created", { name: name.trim() }));
        onOpenChange(false);
        if (!formula && f?.key) onCreated?.(f.key);
      },
      onError: (e) => toastFailure(e, t("rep.saveFailed")),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-155">
        <DialogHeader>
          <DialogTitle>{t(formula ? "rep.formula.editTitle" : "rep.formula.title")}</DialogTitle>
          <DialogDescription>{t("rep.formula.body")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label textCase="none" htmlFor="formula-name">{t("rep.formula.name")}</Label>
            <Input id="formula-name" font="sans" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("rep.formula.namePlaceholder")} autoFocus maxLength={80} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label textCase="none" htmlFor="formula-input">{t("rep.formula.formula")}</Label>
            <FormulaInput
              id="formula-input"
              value={tokens}
              onChange={setTokens}
              metrics={metrics}
              invalid={!!problem}
              placeholder={t("rep.formula.placeholder")}
              labels={{ field: t("rep.formula.formula"), palette: t("rep.formula.palette") }}
            />
            {problem ? <p className="text-xs text-destructive">{t(`rep.formula.error.${problem}` as TKey)}</p> : null}
            {check.error ? <p className="text-xs text-destructive">{check.error instanceof Error ? check.error.message : String(check.error)}</p> : null}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label textCase="none">{t("rep.formula.unit")}</Label>
              <SegmentedControl
                fullWidth
                aria-label={t("rep.formula.unit")}
                value={unit}
                onValueChange={setUnit}
                options={[
                  { value: "number", label: t("rep.formula.unit.number") },
                  { value: "money", label: t("rep.formula.unit.money") },
                  { value: "percent", label: "%" },
                ]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label textCase="none">{t("rep.formula.decimals")}</Label>
              <Select value={String(decimals)} onValueChange={(v) => setDecimals(Number(v))}>
                <SelectTrigger aria-label={t("rep.formula.decimals")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label textCase="none">{t("rep.formula.better")}</Label>
              <SegmentedControl
                fullWidth
                aria-label={t("rep.formula.better")}
                value={better === MetricDirection.LOWER ? "lower" : "higher"}
                onValueChange={(v) => setBetter(v === "lower" ? MetricDirection.LOWER : MetricDirection.HIGHER)}
                options={[
                  { value: "higher", label: t("rep.formula.better.higher") },
                  { value: "lower", label: t("rep.formula.better.lower") },
                ]}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg bg-surface-2 px-3 py-3">
            <span className="text-xs text-muted-foreground">{t("rep.formula.check")}</span>
            <span className="flex-1" />
            <b className="font-mono text-lg font-semibold tabular-nums">{expression ? (check.isLoading ? "…" : checkText) : DASH}</b>
            {expression ? <span className="max-w-65 truncate text-xs text-muted-foreground">{t("rep.formula.checkOver", { text: overTotals })}</span> : null}
          </div>
          <p className="text-xs text-muted-foreground">{t("rep.formula.zero")}</p>
        </div>
        <DialogFooter>
          {formula ? (
            <Button
              variant="danger"
              className="mr-auto"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(formula.id, {
                  onSuccess: () => {
                    toast.success(t("rep.formula.deleted", { name: formula.label }));
                    onOpenChange(false);
                  },
                  onError: (e) => toastFailure(e, t("rep.saveFailed")),
                })
              }
            >
              {t("rep.formula.delete")}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("rep.cancel")}
          </Button>
          <Button disabled={!name.trim() || !expression || !!check.error || save.isPending} onClick={submit}>
            {t(formula ? "rep.formula.save" : "rep.formula.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
